"use client";

import { useState, useRef, useEffect } from "react";
import { Plus, Smile, Pencil, Trash2, Pin, Trophy, BarChart3, DollarSign, PhoneCall, Calendar, Users, TrendingUp, Briefcase, FolderOpen, FileText, Download, Target, UserCheck, CornerDownRight, MessageSquare as MessageSquareIcon, Phone, PhoneIncoming, PhoneOutgoing, Play, Pause, Loader2, Bookmark, Forward, PartyPopper } from "lucide-react";
import { getProxyRecordingUrl, downloadRecording, loadRecordingAsObjectUrl } from "@/app/_module/lib/utils";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/app/_module/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/_module/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";
import { SalesWinCard } from "./SalesWinCard";
import { InsuraTrainingScoreCard } from "./InsuraTrainingScoreCard";
import { EmojiPickerWrapper } from "./EmojiPickerWrapper";
import { AttachmentRenderer } from "./AttachmentRenderer";
import { LinkEmbed } from "./LinkEmbed";
import { getInitials, formatTime, formatMessageText, renderWithLinks, highlightText, getGifFavorites, toggleGifFavorite, MEDIA_LOADED_EVENT } from "./helpers";
import type { Message } from "./TeamChatService";

// ── Call Recording Card ─────────────────────────────────────────────────────

function formatRecordingPhone(phone: string): string {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  if (cleaned.length === 11 && cleaned.startsWith("1")) return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  return phone;
}

function formatRecordingDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function formatRecordingDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })} at ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

function CallRecordingCard({ data }: { data: { leadName?: string; phone?: string; date?: string; duration?: number; direction?: string; recordingUrl?: string; callSid?: string } }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(data.duration || 0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [audioSrc, setAudioSrc] = useState<string>("");
  const [playbackRate, setPlaybackRate] = useState(1);
  const barCount = 40;

  const proxyUrl = getProxyRecordingUrl(data.recordingUrl || null) || data.recordingUrl || "";

  // Load recording bytes via JWT-authed fetch — <audio> can't send Authorization headers
  useEffect(() => {
    if (!proxyUrl) return;
    let revoked = false;
    let createdUrl: string | null = null;
    setIsLoading(true);
    (async () => {
      try {
        const url = await loadRecordingAsObjectUrl(proxyUrl);
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setAudioSrc(url);
      } catch {
        setIsLoading(false);
      }
    })();
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [proxyUrl]);

  // Generate waveform bar heights (cached)
  const [barHeights] = useState(() =>
    Array.from({ length: barCount }, (_, i) => {
      const position = i / barCount;
      const wave1 = Math.sin(position * Math.PI * 2) * 0.3;
      const wave2 = Math.sin(position * Math.PI * 4 + 0.5) * 0.2;
      const wave3 = Math.sin(position * Math.PI * 8 + 1) * 0.15;
      const noise = (Math.sin(i * 12.9898) * 43758.5453) % 1 * 0.35;
      return Math.min(1, 0.3 + Math.abs(wave1 + wave2 + wave3 + noise));
    })
  );

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onLoaded = () => { setAudioDuration(audio.duration); setIsLoading(false); };
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onEnded = () => { setIsPlaying(false); setCurrentTime(0); };
    const onError = () => setIsLoading(false);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [proxyUrl]);

  const effectiveDuration = audioDuration || duration;
  const progress = effectiveDuration > 0 ? currentTime / effectiveDuration : 0;

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch {}
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !effectiveDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    const newTime = pct * effectiveDuration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const cycleSpeed = () => {
    const speeds = [1, 1.5, 2];
    const idx = speeds.indexOf(playbackRate);
    const next = speeds[(idx + 1) % speeds.length];
    setPlaybackRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const handleDownload = async () => {
    const dateStr = data.date ? new Date(data.date).toISOString().split("T")[0] : "unknown";
    const fileName = `recording-${data.leadName || "call"}-${dateStr}.mp3`;
    try {
      await downloadRecording(proxyUrl, fileName);
    } catch {
      // Silent failure here — team-chat audio messages are nice-to-have, not critical workflow.
      // (Toast import isn't already in this file and we don't want to bloat the bundle.)
    }
  };

  const isInbound = data.direction === "inbound" || data.direction === "incoming";

  return (
    <div className="mt-2 max-w-sm" onClick={(e) => e.stopPropagation()}>
      {/* src omitted (undefined, not "") until the JWT-authed blob URL loads —
          an empty src makes the browser re-request the page. */}
      <audio ref={audioRef} src={audioSrc || undefined} preload="metadata" />
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-800 dark:to-zinc-900 shadow-sm hover:shadow-md transition-shadow">
        {/* Header */}
        <div
          className="px-4 py-2.5 flex items-center gap-2"
          style={{ background: "var(--primary, #3b82f6)" }}
        >
          <Phone className="w-4 h-4 text-white/80" />
          <span className="text-xs font-bold text-white/90 uppercase tracking-wider">Call Recording</span>
          <span className={`ml-auto inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
            isInbound
              ? "bg-white/20 text-emerald-100"
              : "bg-white/20 text-blue-100"
          }`}>
            {isInbound ? <PhoneIncoming className="w-3 h-3" /> : <PhoneOutgoing className="w-3 h-3" />}
            {isInbound ? "Inbound" : "Outbound"}
          </span>
        </div>

        {/* Call Info */}
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-bold text-zinc-800 dark:text-zinc-100">
              {data.leadName || "Unknown Caller"}
            </h4>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-zinc-400 dark:text-zinc-500 mb-3">
            <span>{formatRecordingPhone(data.phone || "")}</span>
            <span className="w-1 h-1 rounded-full bg-zinc-300 dark:bg-zinc-600" />
            <span>{data.date ? formatRecordingDate(data.date) : ""}</span>
            <span className="w-1 h-1 rounded-full bg-zinc-300 dark:bg-zinc-600" />
            <span className="font-mono">{formatRecordingDuration(audioDuration || data.duration || 0)}</span>
          </div>

          {/* Waveform Player */}
          <div className="bg-zinc-100 dark:bg-zinc-800/80 rounded-lg p-3">
            {/* Waveform bars */}
            <div
              className="flex items-center justify-between gap-[2px] h-10 cursor-pointer mb-2"
              onClick={handleSeek}
            >
              {barHeights.map((height, index) => {
                const barPos = index / barCount;
                const isPlayed = barPos <= progress;
                return (
                  <div
                    key={index}
                    className={`flex-1 rounded-full min-w-[2px] transition-all duration-150 ${
                      isPlayed
                        ? "bg-blue-600 dark:bg-blue-500"
                        : "bg-zinc-300 dark:bg-zinc-600"
                    } ${isPlaying && isPlayed ? "animate-pulse" : ""}`}
                    style={{
                      height: `${height * 100}%`,
                    }}
                  />
                );
              })}
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={togglePlay}
                disabled={isLoading && !audioDuration}
                className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-300 dark:disabled:bg-zinc-700 text-white transition-colors flex-shrink-0"
              >
                {isLoading && !audioDuration ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-3.5 h-3.5" />
                ) : (
                  <Play className="w-3.5 h-3.5 ml-0.5" />
                )}
              </button>
              <span className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums font-mono min-w-[70px]">
                {formatRecordingDuration(currentTime)} / {formatRecordingDuration(effectiveDuration)}
              </span>
              <button
                onClick={cycleSpeed}
                className="text-[10px] font-bold px-2 py-1 rounded-md bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-300 dark:hover:bg-zinc-600 transition-colors"
              >
                {playbackRate}x
              </button>
              <div className="flex-1" />
              <button
                onClick={handleDownload}
                className="p-1.5 rounded-md hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                title="Download recording"
              >
                <Download className="w-3.5 h-3.5 text-zinc-400" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export interface MessageItemProps {
  message: Message;
  isOwn: boolean;
  currentUserId?: string;
  onReact: (emoji: string) => void;
  onDelete: () => void;
  onEdit: (content: string) => void;
  onPin?: () => void;
  onReply?: () => void;
  onViewThread?: () => void;
  onBookmark?: () => void;
  onForward?: () => void;
  onSendGif?: (gifUrl: string) => void;
  readReceipts?: Record<string, string>;
  allMembers?: Array<{ userId: string; name: string; profileImageUrl?: string; agencyRole?: string; agencyName?: string }>;
  isLastOwnMessage?: boolean;
  searchHighlight?: string;
  isAdminOrOwner?: boolean;
  isBookmarked?: boolean;
  isHighlighted?: boolean;
}

/** Celebration set offered on a sales win — kept in one place so the zero-state
 *  row and the hover toolbar always offer the same emoji. */
const SALES_WIN_QUICK_REACTIONS = ["\uD83D\uDD25", "\uD83C\uDFC6", "\uD83D\uDC4F", "\uD83D\uDCB0"];

export function MessageItem({
  message,
  isOwn,
  currentUserId,
  onReact,
  onDelete,
  onEdit,
  onPin,
  onReply,
  onViewThread,
  onBookmark,
  onForward,
  onSendGif,
  readReceipts,
  allMembers,
  isLastOwnMessage,
  searchHighlight,
  isAdminOrOwner,
  isBookmarked,
  isHighlighted,
}: MessageItemProps) {
  const [actionsLocked, setActionsLocked] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content || "");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Agency-wide: show sender's agency name when members are from multiple agencies
  const senderAgency = allMembers?.find(m => m.userId === message.senderId)?.agencyName;
  const uniqueAgencies = new Set(allMembers?.map(m => m.agencyName).filter(Boolean));
  const showSenderAgency = uniqueAgencies.size > 1 && senderAgency;

  // Delete permission: own messages within 10 min OR agency owner/admin can delete any message
  const messageAge = Date.now() - new Date(message.createdAt).getTime();
  const tenMinutes = 10 * 60 * 1000;
  const canDeleteOwn = isOwn && messageAge < tenMinutes;
  const canDelete = canDeleteOwn || !!isAdminOrOwner;

  if (message.messageType === "sales_win") {
    const salesWinReactionGroups = (message.reactions || []).reduce((acc, r) => {
      if (!acc[r.emoji]) acc[r.emoji] = { names: [], userIds: [] };
      acc[r.emoji].names.push(r.user?.name || "Unknown");
      acc[r.emoji].userIds.push(r.userId);
      return acc;
    }, {} as Record<string, { names: string[]; userIds: string[] }>);

    return (
      <div className="group/msg relative">
        <SalesWinCard message={message} isOwn={isOwn} />
        {/* Reactions.
            Previously this row only rendered once a win ALREADY had a reaction,
            and the only way to leave the first one was the hover toolbar below —
            which never appears on a touch device. So on phones a win could
            never receive its first reaction, and on desktop nothing invited one.
            The row now always renders: with no reactions yet it offers the
            celebratory set as one tap. */}
        <div className="px-4 pl-[52px]">
          {Object.keys(salesWinReactionGroups).length === 0 && (
            <div className="mb-1 mt-1 flex flex-wrap items-center gap-1">
              {SALES_WIN_QUICK_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => onReact(emoji)}
                  aria-label={`React with ${emoji}`}
                  className="inline-flex h-7 items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 px-2 text-base leading-none transition-all hover:-translate-y-0.5 hover:border-amber-300 hover:bg-amber-50 dark:border-zinc-700 dark:bg-zinc-800/60 dark:hover:border-amber-500/40 dark:hover:bg-amber-900/20"
                >
                  {emoji}
                </button>
              ))}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    aria-label="Add a reaction"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800/60 dark:hover:bg-zinc-700"
                  >
                    <Plus className="h-3.5 w-3.5 text-zinc-400" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto border-none p-0 shadow-xl" side="top" align="start">
                  <EmojiPickerWrapper onSelect={onReact} />
                </PopoverContent>
              </Popover>
              <span className="ml-1 select-none text-[11px] text-zinc-400 dark:text-zinc-500">
                Hype them up
              </span>
            </div>
          )}
          {Object.keys(salesWinReactionGroups).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1 mb-1">
              {Object.entries(salesWinReactionGroups).map(([emoji, { names, userIds }]) => {
                const myReaction = currentUserId && userIds.includes(currentUserId);
                return (
                  <button
                    key={emoji}
                    onClick={() => onReact(emoji)}
                    className={`group/react inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-sm transition-all ${
                      myReaction
                        ? "bg-blue-500/10 border border-blue-500/40 hover:bg-blue-500/20"
                        : "bg-zinc-100 dark:bg-zinc-800/80 border border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600 hover:bg-zinc-200 dark:hover:bg-zinc-700"
                    }`}
                    title={names.join(", ")}
                  >
                    <span className="text-base leading-none">{emoji}</span>
                    <span className={`text-xs font-medium ${myReaction ? "text-blue-600 dark:text-blue-400" : "text-zinc-500 dark:text-zinc-400"}`}>
                      {names.length}
                    </span>
                  </button>
                );
              })}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-zinc-100 dark:bg-zinc-800/80 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors">
                    <Plus className="w-3.5 h-3.5 text-zinc-400" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 border-none shadow-xl" side="top" align="start">
                  <EmojiPickerWrapper onSelect={onReact} />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
        {/* Quick reaction toolbar on hover */}
        <div className="invisible group-hover/msg:visible absolute right-4 -top-3 flex items-center gap-0.5 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg px-1 py-0.5 z-10">
          {SALES_WIN_QUICK_REACTIONS.map(e => (
            <button key={e} onClick={() => onReact(e)} className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors text-sm">
              {e}
            </button>
          ))}
          {onSendGif && (
            <button onClick={() => onSendGif('https://media.tenor.com/m_Wep7xxA6gAAAAC/wolf-of.gif')} className="p-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors" title="Pick up the phone!">
              <Phone className="w-3.5 h-3.5 text-amber-500" />
            </button>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <button className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors">
                <Smile className="w-3.5 h-3.5 text-zinc-400" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" side="top" align="end">
              <EmojiPickerWrapper onSelect={onReact} />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    );
  }

  if (message.messageType === "insurasim_score") {
    const simReactionGroups = (message.reactions || []).reduce((acc, r) => {
      if (!acc[r.emoji]) acc[r.emoji] = { names: [], userIds: [] };
      acc[r.emoji].names.push(r.user?.name || "Unknown");
      acc[r.emoji].userIds.push(r.userId);
      return acc;
    }, {} as Record<string, { names: string[]; userIds: string[] }>);

    return (
      <div className="group/msg relative">
        <InsuraTrainingScoreCard message={message} />
        <div className="px-4 pl-[52px]">
          {Object.keys(simReactionGroups).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1 mb-1">
              {Object.entries(simReactionGroups).map(([emoji, group]) => (
                <button key={emoji} onClick={() => onReact?.(emoji)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors ${group.userIds.includes(currentUserId || '') ? 'bg-blue-500/10 border-blue-500/40 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20' : 'bg-zinc-100 dark:bg-zinc-800/80 border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:border-zinc-300 dark:hover:border-zinc-600'}`}>
                  <span>{emoji}</span>
                  <span className="text-[10px]">{group.names.length}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (message.messageType === "system") {
    return (
      <div className="flex justify-center py-2">
        <span className="text-xs text-zinc-400 dark:text-zinc-500 bg-zinc-100 dark:bg-zinc-800/50 px-3 py-1 rounded-full">
          {message.sender?.name} {message.content}
        </span>
      </div>
    );
  }

  // Group reactions by emoji — track names AND user IDs for toggle
  const reactionGroups = (message.reactions || []).reduce((acc, r) => {
    if (!acc[r.emoji]) acc[r.emoji] = { names: [], userIds: [] };
    acc[r.emoji].names.push(r.user?.name || "Unknown");
    acc[r.emoji].userIds.push(r.userId);
    return acc;
  }, {} as Record<string, { names: string[]; userIds: string[] }>);

  // Read receipts: show who has read this message (only on last own message)
  const readByMembers = (() => {
    if (!isOwn || !isLastOwnMessage || !readReceipts || !allMembers || !currentUserId) return [];
    const msgTime = new Date(message.createdAt).getTime();
    return allMembers
      .filter(m => {
        if (m.userId === currentUserId) return false;
        const lastRead = readReceipts[m.userId];
        if (!lastRead) return false;
        return new Date(lastRead).getTime() >= msgTime;
      })
      .map(m => ({ ...m, readAt: readReceipts[m.userId] }));
  })();

  return (
    <div
      role="article"
      aria-label={`Message from ${message.sender?.name || message.sender?.email || "unknown"} at ${formatTime(message.createdAt)}`}
      className={`group/msg flex gap-3 px-4 py-1.5 pb-2 transition-all relative ${
        isHighlighted ? 'bg-amber-100/60 dark:bg-amber-900/20 ring-1 ring-amber-400/50 animate-[highlight-flash_2s_ease-out]' :
        // Full row highlight when message has @everyone/@here or mentions you (like Discord/Slack)
        (message.content?.includes('@everyone') || message.content?.includes('@here') || (!isOwn && currentUserId && message.content?.includes(`<@${currentUserId}:`)))
          ? 'bg-amber-50 dark:bg-[#2e2a1f] border-l-[3px] border-amber-500 pl-[13px] hover:bg-amber-100/80 dark:hover:bg-[#36311f]'
          : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/30'
      }`}
      style={isHighlighted ? { animation: 'highlight-flash 2s ease-out' } : undefined}
    >
      <Avatar className="w-8 h-8 mt-0.5 flex-shrink-0">
        <AvatarImage src={message.sender?.profileImageUrl} />
        <AvatarFallback className="text-xs bg-blue-600 text-white font-semibold">
          {getInitials(message.sender?.name || message.sender?.email || "?")}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-semibold text-sm text-zinc-900 dark:text-zinc-100">
            {message.sender?.name || message.sender?.email}
          </span>
          {showSenderAgency && (
            <span className="text-[11px] text-zinc-400 dark:text-zinc-500 relative -top-[1px]">({senderAgency})</span>
          )}
          <span className="text-[11px] text-zinc-400 dark:text-zinc-500">
            {formatTime(message.createdAt)}
          </span>
          {message.isEdited && (
            <span className="text-[10px] text-zinc-400 dark:text-zinc-500">(edited)</span>
          )}
        </div>
        {/* Reply context — if this message is a reply (Discord-style) */}
        {message.parentMessageId && message.parentMessage && (
          <button
            onClick={onViewThread}
            className="flex items-start gap-2 mb-1 -mt-0.5 group/reply pl-2 border-l-2 border-blue-400/50 hover:border-blue-500 rounded-r-sm transition-colors max-w-md"
          >
            <CornerDownRight className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <span className="text-[11px] font-semibold text-blue-500 dark:text-blue-400">
                {message.parentMessage.sender?.name || "someone"}
              </span>
              {message.parentMessage.content && (
                <p className="text-[11px] text-zinc-400 dark:text-zinc-500 truncate group-hover/reply:text-zinc-500 dark:group-hover/reply:text-zinc-400 transition-colors">
                  {message.parentMessage.content
                    .replace(/<@[^>]+>/g, (m) => { const n = m.match(/:(.+)>/); return n ? `@${n[1]}` : '@someone'; })
                    .replace(/::cmd::.*/g, '[Command]')
                    .replace(/::resource::.*/g, '[Resource]').replace(/::recording::.*/g, '[Recording]')
                    .replace(/::sticker::.*/g, '[Sticker]')
                .replace(/::meme::.*/g, '[Meme]')
                .replace(/::clip::.*/g, '[Clip]')
                    .replace(/::clip::.*/g, '[Clip]')
                    .replace(/::meme::.*/g, '[Meme]')
                    .slice(0, 100)}
                </p>
              )}
            </div>
          </button>
        )}
        {isEditing ? (
          <div className="mt-1 flex gap-2">
            <Input
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onEdit(editContent);
                  setIsEditing(false);
                }
                if (e.key === "Escape") setIsEditing(false);
              }}
              className="text-sm"
              autoFocus
            />
            <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>Cancel</Button>
            <Button size="sm" onClick={() => { onEdit(editContent); setIsEditing(false); }}>Save</Button>
          </div>
        ) : (message.content || "").startsWith("::resource::") ? (
          /* Resource card — rich embed */
          (() => {
            try {
              const res = JSON.parse((message.content || "").replace("::resource::", ""));
              return (
                <div className="mt-2 max-w-sm">
                  <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-800 dark:to-zinc-900 shadow-sm hover:shadow-md transition-shadow">
                    {/* Header bar */}
                    <div className="px-4 py-2 flex items-center gap-2" style={{ background: 'var(--primary, #3b82f6)' }}>
                      <FolderOpen className="w-4 h-4 text-white/80" />
                      <span className="text-xs font-semibold text-white/90 uppercase tracking-wider">Shared Resource</span>
                    </div>
                    {/* Content */}
                    <div className="px-4 py-3">
                      <h4 className="text-sm font-bold text-zinc-800 dark:text-zinc-100 mb-1">{res.title}</h4>
                      {res.description && (
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2 line-clamp-2">{res.description}</p>
                      )}
                      <div className="flex items-center gap-1.5 flex-wrap mb-3">
                        {res.category && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                            {res.category}
                          </span>
                        )}
                        {res.carrier && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                            {res.carrier}
                          </span>
                        )}
                        {res.scope && (
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            res.scope === 'personal'
                              ? "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                              : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                          }`}>
                            {res.scope === 'personal' ? 'Personal' : res.scope === 'agency-only' ? 'Agency' : res.scope === 'agency-wide' ? 'Agency-Wide' : res.scope}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {res.shareUrl && (
                          <button
                            onClick={async () => {
                              try {
                                // Same as resources page — fetch blob, trigger download with filename
                                const response = await fetch(res.shareUrl);
                                if (!response.ok) throw new Error('Download failed');
                                const blob = await response.blob();
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = res.title || 'download';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                setTimeout(() => URL.revokeObjectURL(url), 100);
                              } catch {}
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" />
                            Download
                          </button>
                        )}
                        {res.shareUrl && (
                          <button
                            onClick={async () => {
                              try {
                                // Same as resources page — fetch blob, open blob URL (hides Supabase)
                                const response = await fetch(res.shareUrl);
                                if (!response.ok) throw new Error('Failed to load');
                                const blob = await response.blob();
                                const blobUrl = URL.createObjectURL(blob);
                                window.open(blobUrl, '_blank', 'noopener,noreferrer');
                                setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                              } catch {}
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-200 hover:bg-zinc-300 dark:bg-zinc-700 dark:hover:bg-zinc-600 text-zinc-700 dark:text-zinc-200 text-xs font-medium transition-colors"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            View
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            } catch {
              return <p className="text-sm text-zinc-700 dark:text-zinc-300">{message.content}</p>;
            }
          })()
        ) : (message.content || "").startsWith("::recording::") ? (
          /* Call recording card — rich embed with inline player */
          (() => {
            try {
              const rec = JSON.parse((message.content || "").replace("::recording::", ""));
              return <CallRecordingCard data={rec} />;
            } catch {
              return <p className="text-sm text-zinc-700 dark:text-zinc-300">{message.content}</p>;
            }
          })()
        ) : (message.content || "").startsWith("::cmd::") ? (
          /* Slash command rich card */
          (() => {
            try {
              const cmd = JSON.parse((message.content || "").replace("::cmd::", ""));
              const brandBg = 'var(--primary, #3b82f6)';

              if (cmd.type === "leaderboard") {
                const top3 = cmd.agents.slice(0, 3);
                const rest = cmd.agents.slice(3);
                const podiumColors = [
                  { bg: "bg-gradient-to-b from-amber-400 to-amber-500", ring: "ring-amber-400", text: "text-amber-900", label: "GOLD", height: "h-20" },
                  { bg: "bg-gradient-to-b from-zinc-300 to-zinc-400", ring: "ring-zinc-300", text: "text-zinc-700", label: "SILVER", height: "h-16" },
                  { bg: "bg-gradient-to-b from-amber-600 to-amber-700", ring: "ring-amber-600", text: "text-amber-100", label: "BRONZE", height: "h-14" },
                ];
                // Display order: silver(1), gold(0), bronze(2) — podium style
                const podiumOrder = top3.length >= 3 ? [1, 0, 2] : top3.map((_: any, i: number) => i);

                return (
                  <div className="mt-2 max-w-md">
                    <div className="rounded-2xl overflow-hidden shadow-lg">
                      <div className="px-4 py-3 flex items-center gap-2" style={{ background: brandBg }}>
                        <Trophy className="w-5 h-5 text-white" />
                        <span className="text-white font-bold text-sm">Team Leaderboard</span>
                        <span className="ml-auto text-[10px] text-white/70 font-medium">{cmd.period}</span>
                      </div>

                      {cmd.agents.length === 0 ? (
                        <div className="bg-white dark:bg-zinc-800 px-4 py-8 text-center text-sm text-zinc-400">
                          No sales yet — start closing!
                        </div>
                      ) : (
                        <div className="bg-white dark:bg-zinc-800">
                          {/* Podium — top 3 */}
                          {top3.length > 0 && (
                            <div className="px-4 pt-4 pb-2">
                              <div className="flex items-end justify-center gap-3">
                                {podiumOrder.map((idx: number) => {
                                  const agent = top3[idx];
                                  if (!agent) return null;
                                  const style = podiumColors[idx];
                                  return (
                                    <div key={idx} className="flex flex-col items-center" style={{ width: idx === 0 ? '110px' : '90px' }}>
                                      {/* Avatar */}
                                      <div className={`w-10 h-10 rounded-full ${style.bg} flex items-center justify-center text-white font-bold text-sm shadow-lg ring-2 ${style.ring} mb-1`}>
                                        {getInitials(agent.name)}
                                      </div>
                                      <p className="text-xs font-bold text-zinc-800 dark:text-zinc-200 truncate w-full text-center">{agent.name?.split(' ')[0]}</p>
                                      <p className="text-[10px] text-zinc-400">{agent.sales} sold</p>
                                      {agent.premium > 0 && <p className="text-[10px] font-semibold text-emerald-500">${agent.premium.toLocaleString()}</p>}
                                      {/* Podium bar */}
                                      <div className={`w-full ${style.height} ${style.bg} rounded-t-lg mt-1 flex items-center justify-center`}>
                                        <span className="text-[9px] font-black text-white/80">{style.label}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Rest of agents */}
                          {rest.length > 0 && (
                            <div className="divide-y divide-zinc-100 dark:divide-zinc-700 border-t border-zinc-100 dark:border-zinc-700">
                              {rest.map((a: any, i: number) => (
                                <div key={i} className="flex items-center gap-3 px-4 py-2">
                                  <span className="text-xs font-bold text-zinc-400 w-6 text-center">{i + 4}</span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300 truncate">{a.name}</p>
                                  </div>
                                  <p className="text-xs font-semibold text-zinc-600 dark:text-zinc-400">{a.sales} sold</p>
                                  {a.premium > 0 && <p className="text-xs text-emerald-500 font-semibold">${a.premium.toLocaleString()}</p>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              if (cmd.type === "mystats") {
                const statItems = [
                  { label: "Policies Sold", value: cmd.policies || cmd.sales, Icon: Trophy, bg: "bg-amber-50 dark:bg-amber-900/20", iconColor: "text-amber-500", color: "text-amber-600 dark:text-amber-400" },
                  { label: "Premium", value: `$${(cmd.premium || 0).toLocaleString()}`, Icon: DollarSign, bg: "bg-emerald-50 dark:bg-emerald-900/20", iconColor: "text-emerald-500", color: "text-emerald-600 dark:text-emerald-400" },
                  { label: "Calls Made", value: cmd.calls, Icon: PhoneCall, bg: "bg-blue-50 dark:bg-blue-900/20", iconColor: "text-blue-500", color: "text-blue-600 dark:text-blue-400" },
                  { label: "Appointments", value: cmd.appointments, Icon: Calendar, bg: "bg-purple-50 dark:bg-purple-900/20", iconColor: "text-purple-500", color: "text-purple-600 dark:text-purple-400" },
                  { label: "Leads", value: cmd.leads, Icon: Users, bg: "bg-cyan-50 dark:bg-cyan-900/20", iconColor: "text-cyan-500", color: "text-cyan-600 dark:text-cyan-400" },
                  { label: "Conv Rate", value: `${(cmd.convRate || cmd.conversion || 0).toFixed(1)}%`, Icon: TrendingUp, bg: "bg-orange-50 dark:bg-orange-900/20", iconColor: "text-orange-500", color: "text-orange-600 dark:text-orange-400" },
                  { label: "Close Rate", value: `${(cmd.closeRate || 0).toFixed(1)}%`, Icon: Target, bg: "bg-rose-50 dark:bg-rose-900/20", iconColor: "text-rose-500", color: "text-rose-600 dark:text-rose-400" },
                  { label: "Avg Premium", value: `$${(cmd.avgPremium || 0).toLocaleString()}`, Icon: DollarSign, bg: "bg-indigo-50 dark:bg-indigo-900/20", iconColor: "text-indigo-500", color: "text-indigo-600 dark:text-indigo-400" },
                ];
                return (
                  <div className="mt-2 max-w-sm">
                    <div className="rounded-2xl overflow-hidden shadow-lg">
                      <div className="px-4 py-3 flex items-center gap-2" style={{ background: brandBg }}>
                        <BarChart3 className="w-5 h-5 text-white" />
                        <div>
                          <span className="text-white font-bold text-sm">{cmd.name}&apos;s Stats</span>
                          {cmd.rank > 0 && <span className="ml-2 text-[10px] bg-white/20 text-white px-1.5 py-0.5 rounded-full font-bold">Rank #{cmd.rank}</span>}
                        </div>
                        <span className="ml-auto text-[10px] text-white/70 font-medium">{cmd.period}</span>
                      </div>
                      <div className="bg-white dark:bg-zinc-800 grid grid-cols-2 gap-px bg-zinc-100 dark:bg-zinc-700">
                        {statItems.map((s, i) => (
                          <div key={i} className="bg-white dark:bg-zinc-800 px-4 py-3 flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-xl ${s.bg} flex items-center justify-center flex-shrink-0`}>
                              <s.Icon className={`w-4.5 h-4.5 ${s.iconColor}`} />
                            </div>
                            <div>
                              <p className={`text-base font-black ${s.color}`}>{s.value}</p>
                              <p className="text-[10px] text-zinc-400 uppercase tracking-wider font-semibold">{s.label}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              if (cmd.type === "premium") {
                return (
                  <div className="mt-2 max-w-sm">
                    <div className="rounded-2xl overflow-hidden shadow-lg">
                      <div className="px-4 py-3 flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600">
                        <DollarSign className="w-5 h-5 text-white" />
                        <span className="text-white font-bold text-sm">Premium Report</span>
                        <span className="ml-auto text-[10px] text-white/70 font-medium">{cmd.period}</span>
                      </div>
                      <div className="bg-white dark:bg-zinc-800 px-5 py-4">
                        <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400 mb-1">${(cmd.total || 0).toLocaleString()}</p>
                        <p className="text-xs text-zinc-400 mb-3">Total Premium Collected</p>
                        <div className="flex gap-4">
                          <div>
                            <p className="text-lg font-bold text-zinc-800 dark:text-zinc-200">{cmd.sales}</p>
                            <p className="text-[10px] text-zinc-400 uppercase">Policies Sold</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              if (cmd.type === "pipeline") {
                const items = [
                  { label: "Leads", value: cmd.leads, icon: "users", bg: "bg-blue-500" },
                  { label: "Appointments", value: cmd.appointments, icon: "calendar", bg: "bg-purple-500" },
                  { label: "Calls Made", value: cmd.calls, icon: "phone", bg: "bg-cyan-500" },
                  { label: "Policies Sold", value: cmd.sold, icon: "check", bg: "bg-emerald-500" },
                ];
                const iconMap: Record<string, any> = { users: Users, calendar: Calendar, phone: PhoneCall, check: Target };
                return (
                  <div className="mt-2 max-w-sm">
                    <div className="rounded-2xl overflow-hidden shadow-lg">
                      <div className="px-4 py-3 flex items-center gap-2" style={{ background: brandBg }}>
                        <Briefcase className="w-5 h-5 text-white" />
                        <span className="text-white font-bold text-sm">Pipeline Summary</span>
                        <span className="ml-auto text-[10px] text-white/70 font-medium">{cmd.period}</span>
                      </div>
                      <div className="bg-white dark:bg-zinc-800 px-4 py-3 space-y-2.5">
                        {items.map((item, i) => {
                          const IconComp = iconMap[item.icon] || Users;
                          return (
                            <div key={i} className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg ${item.bg} flex items-center justify-center`}><IconComp className="w-4 h-4 text-white" /></div>
                              <div className="flex-1">
                                <p className="text-xs text-zinc-400 uppercase tracking-wider font-semibold">{item.label}</p>
                              </div>
                              <p className="text-lg font-black text-zinc-800 dark:text-zinc-200">{(item.value || 0).toLocaleString()}</p>
                            </div>
                          );
                        })}
                        {cmd.premium > 0 && (
                          <>
                            <div className="h-px bg-zinc-100 dark:bg-zinc-700" />
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-zinc-400 font-semibold">Total Premium</span>
                              <span className="text-lg font-black text-emerald-500">${(cmd.premium || 0).toLocaleString()}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              return <p className="text-sm text-zinc-700 dark:text-zinc-300">{message.content}</p>;
            } catch {
              return <p className="text-sm text-zinc-700 dark:text-zinc-300">{message.content}</p>;
            }
          })()
        ) : (message.content || "").startsWith("::clip::") ? (
          /* Clip — a short mp4. Muted + loop + playsInline so it behaves like
             the GIF beside it instead of blasting audio into an open office;
             controls are there for anyone who wants the sound. Fixed height for
             the same scroll reason as the GIF below. */
          <video
            src={(message.content || "").replace("::clip::", "")}
            className="mt-1 h-48 w-auto max-w-sm rounded-lg bg-black object-contain"
            muted
            loop
            playsInline
            controls
            preload="metadata"
            onLoadedData={e => e.currentTarget.dispatchEvent(new CustomEvent(MEDIA_LOADED_EVENT, { bubbles: true }))}
          />
        ) : (message.content || "").startsWith("::meme::") ? (
          /* Meme — an image macro, so it has to be big enough to READ. Fixed
             height for the same reason as the GIF below: an unsized image
             measures zero until it decodes and the list lands above it. */
          <img
            src={(message.content || "").replace("::meme::", "")}
            alt="Meme"
            className="mt-1 h-64 w-auto max-w-sm rounded-lg object-contain object-left"
            onLoad={e => e.currentTarget.dispatchEvent(new CustomEvent(MEDIA_LOADED_EVENT, { bubbles: true }))}
          />
        ) : (message.content || "").startsWith("::sticker::") ? (
          /* Sticker — transparent, and deliberately smaller than a GIF.
             A KLIPY sticker carries its URL in the tag. An AI-generated one is
             an uploaded attachment, so the tag is bare and the image comes from
             AttachmentRenderer below — nothing to draw here. */
          (message.content || "").replace("::sticker::", "").trim() ? (
            <img
              src={(message.content || "").replace("::sticker::", "")}
              alt="Sticker"
              className="mt-1 h-32 w-32 object-contain"
              onLoad={e => e.currentTarget.dispatchEvent(new CustomEvent(MEDIA_LOADED_EVENT, { bubbles: true }))}
            />
          ) : null
        ) : (message.content || "").match(/^https?:\/\/.*\.(gif|tenor\.com)/i) ? (
          /* GIF URL — render as image with favorite star.
             `h-48` is what keeps the list from jumping: with only max-height the
             img measures ZERO until it decodes, so a scroll-to-bottom lands
             above the GIF you just sent and you have to scroll down to find it.
             A fixed height reserves the row up front — the same reason the
             sticker above (h-32 w-32) never had the problem. Width stays auto,
             so odd aspect ratios are not letterboxed, and growing sideways
             costs the vertical scroll nothing.
             Not lazy: this is the live end of the channel, it is on screen. */
          <div className="relative group/chatgif inline-block mt-1">
            <img
              src={message.content!}
              alt="GIF"
              className="h-48 w-auto max-w-xs rounded-lg object-contain object-left"
              onLoad={e => e.currentTarget.dispatchEvent(new CustomEvent(MEDIA_LOADED_EVENT, { bubbles: true }))}
            />
            <button
              onClick={() => {
                const gifUrl = message.content!;
                const gifId = gifUrl.split('/').pop()?.split('?')[0] || gifUrl;
                toggleGifFavorite({ id: gifId, url: gifUrl, preview: gifUrl });
              }}
              className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center bg-black/60 text-white opacity-0 group-hover/chatgif:opacity-100 hover:bg-amber-500 transition-all"
              title={getGifFavorites().some(f => f.url === message.content) ? "Remove from favorites" : "Save to favorites"}
            >
              <span className="text-sm">{"\u2606"}</span>
            </button>
          </div>
        ) : (
          <p className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap break-words leading-relaxed">
            {(message.content || "").split(/(@everyone|@here|<@[^>]+>)/g).map((part, i) => {
              if (part === "@everyone" || part === "@here") {
                return (
                  <span key={i} className="inline-block px-1.5 py-0.5 rounded bg-blue-500/20 dark:bg-blue-500/30 text-blue-600 dark:text-blue-300 font-semibold text-xs mx-0.5">
                    {part}
                  </span>
                );
              }
              // Parse <@agency:uuid:Name> agency mention format
              const agencyMatch = part.match(/^<@agency:([^:]+):(.+)>$/);
              if (agencyMatch) {
                return (
                  <span key={i} className="inline-block px-1.5 py-0.5 rounded bg-amber-500/20 dark:bg-amber-500/30 text-amber-600 dark:text-amber-300 font-semibold text-xs mx-0.5">
                    @{agencyMatch[2]}
                  </span>
                );
              }
              // Parse <@userId:Name> mention format
              const mentionMatch = part.match(/^<@([^:]+):(.+)>$/);
              if (mentionMatch) {
                return (
                  <span key={i} className="inline-block px-1.5 py-0.5 rounded bg-blue-500/20 dark:bg-blue-500/30 text-blue-600 dark:text-blue-300 font-semibold text-xs mx-0.5 cursor-pointer hover:bg-blue-500/30 dark:hover:bg-blue-500/40">
                    @{mentionMatch[2]}
                  </span>
                );
              }
              // Apply markdown formatting + link detection + search highlight
              const formatted = formatMessageText(part);
              return formatted.map((node, j) => {
                if (typeof node === 'string') {
                  if (searchHighlight) {
                    return <span key={`${i}-${j}`}>{highlightText(node, searchHighlight)}</span>;
                  }
                  return <span key={`${i}-${j}`}>{renderWithLinks(node)}</span>;
                }
                return node;
              });
            })}
          </p>
        )}
        {/* Link embeds — extract URLs from content and render previews */}
        {(() => {
          const content = message.content || '';
          // Don't embed if content is a special format (resource, recording, cmd, gif)
          if (content.startsWith('::')) return null;
          const urls = content.match(/https?:\/\/[^\s<]+/g);
          if (!urls) return null;
          // Dedupe and skip GIF URLs (already rendered as images)
          const unique = [...new Set(urls)].filter(u => !u.match(/\.(gif|tenor\.com)/i));
          return unique.map((url, i) => <LinkEmbed key={`embed-${i}`} url={url} />);
        })()}
        {/* Attachments */}
        <AttachmentRenderer
          attachments={message.attachments}
          metadata={message.metadata}
          asSticker={(message.content || "").startsWith("::sticker::")}
        />
        {/* Reply count — click to see all replies in side panel */}
        {(message.replyCount || 0) > 0 && (
          <button
            onClick={onViewThread}
            className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-md text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors group/replies"
          >
            <MessageSquareIcon className="w-3.5 h-3.5" />
            <span>{message.replyCount} {message.replyCount === 1 ? 'reply' : 'replies'}</span>
            <span className="text-[10px] text-zinc-400 group-hover/replies:text-blue-400 transition-colors">View conversation</span>
          </button>
        )}
        {/* Reactions */}
        {/* Read receipts */}
        {readByMembers.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-0.5 mt-1.5 hover:opacity-80 transition-opacity cursor-pointer" aria-label={`Read by ${readByMembers.length} people`}>
                {readByMembers.slice(0, 5).map((m, i) => (
                  <div key={m.userId} className="relative" style={{ marginLeft: i > 0 ? '-4px' : 0, zIndex: 5 - i }}>
                    <Avatar className="w-4 h-4 ring-2 ring-white dark:ring-zinc-900">
                      <AvatarImage src={m.profileImageUrl} />
                      <AvatarFallback className="text-[6px] bg-zinc-300 dark:bg-zinc-600 text-zinc-700 dark:text-zinc-200 font-semibold">
                        {getInitials(m.name)}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                ))}
                {readByMembers.length > 5 && (
                  <span className="text-[10px] text-zinc-400 ml-1">+{readByMembers.length - 5}</span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="start" className="w-72 p-0 shadow-xl border-zinc-200 dark:border-zinc-700">
              <div className="px-3 py-2 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between">
                <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">Seen by</span>
                <span className="text-[10px] font-medium text-zinc-400 bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded-full">{readByMembers.length}</span>
              </div>
              <div className="max-h-[240px] overflow-y-auto py-1">
                {readByMembers.map(m => {
                  const readTime = m.readAt ? new Date(m.readAt) : null;
                  const timeStr = readTime ? readTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
                  const dateStr = readTime ? readTime.toLocaleDateString([], { month: 'short', day: 'numeric' }) : '';
                  const roleColors: Record<string, string> = {
                    owner: 'text-amber-600 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400',
                    admin: 'text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400',
                    agent: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400',
                  };
                  return (
                    <div key={m.userId} className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                      <Avatar className="w-7 h-7 flex-shrink-0">
                        <AvatarImage src={m.profileImageUrl} />
                        <AvatarFallback className="text-[9px] bg-blue-600 text-white font-semibold">
                          {getInitials(m.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200 truncate">{m.name}</span>
                          {m.agencyRole && (
                            <span className={`text-[8px] font-bold px-1 py-0.5 rounded ${roleColors[m.agencyRole] || 'text-zinc-400 bg-zinc-100 dark:bg-zinc-800'}`}>
                              {m.agencyRole.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {m.agencyName && (
                            <span className="text-[10px] text-zinc-400 dark:text-zinc-500 truncate">{m.agencyName}</span>
                          )}
                          {m.agencyName && readTime && <span className="text-zinc-300 dark:text-zinc-600 text-[8px]">·</span>}
                          {readTime && (
                            <span className="text-[10px] text-zinc-400 dark:text-zinc-500 whitespace-nowrap">{dateStr} {timeStr}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}
        {Object.keys(reactionGroups).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2" role="group" aria-label="Reactions">
            {Object.entries(reactionGroups).map(([emoji, { names, userIds }]) => {
              const myReaction = currentUserId ? userIds.includes(currentUserId) : false;
              return (
                <button
                  key={emoji}
                  onClick={() => onReact(emoji)}
                  className={`group/react inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-sm transition-all ${
                    myReaction
                      ? "bg-blue-500/10 border border-blue-500/40 hover:bg-blue-500/20"
                      : "bg-zinc-100 dark:bg-zinc-800/80 border border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600 hover:bg-zinc-200 dark:hover:bg-zinc-700"
                  }`}
                  title={names.join(", ")}
                >
                  <span className="text-base leading-none">{emoji}</span>
                  <span className={`text-xs font-medium ${myReaction ? "text-blue-600 dark:text-blue-400" : "text-zinc-500 dark:text-zinc-400"}`}>
                    {names.length}
                  </span>
                </button>
              );
            })}
            {/* Add reaction button */}
            <Popover>
              <PopoverTrigger asChild>
                <button className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-zinc-100 dark:bg-zinc-800/80 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors">
                  <Plus className="w-3.5 h-3.5 text-zinc-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 border-none shadow-xl" side="top" align="start">
                <EmojiPickerWrapper onSelect={onReact} />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>
      {/* Message actions — visible on hover OR when emoji picker is open */}
      <div role="toolbar" aria-label="Message actions" className={`${actionsLocked ? "visible" : "invisible group-hover/msg:visible"} absolute right-4 -top-3 flex items-center gap-0.5 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg px-1 py-0.5 z-10`}>
        {/* Quick reactions */}
        {["\uD83D\uDC4D", "\uD83D\uDD25", "\uD83C\uDFC6", "\uD83D\uDE02"].map(e => (
          <button key={e} onClick={() => onReact(e)} aria-label={`React with ${e}`} className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors text-sm">
            {e}
          </button>
        ))}
        {onSendGif && (
          <button onClick={() => onSendGif('https://media.tenor.com/m_Wep7xxA6gAAAAC/wolf-of.gif')} className="p-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors" title="Pick up the phone!" aria-label="Pick up the phone!">
            <Phone className="w-3.5 h-3.5 text-amber-500" />
          </button>
        )}
        <Popover onOpenChange={setActionsLocked}>
          <PopoverTrigger asChild>
            <button aria-label="Pick emoji reaction" className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors">
              <Smile className="w-3.5 h-3.5 text-zinc-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" side="top" align="end">
            <EmojiPickerWrapper onSelect={(emoji) => { onReact(emoji); setActionsLocked(false); }} />
          </PopoverContent>
        </Popover>
        {onReply && (
          <button
            onClick={onReply}
            className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
            title="Reply"
          >
            <CornerDownRight className="w-3.5 h-3.5 text-zinc-400" />
          </button>
        )}
        {onBookmark && (
          <button
            onClick={onBookmark}
            className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
            title={isBookmarked ? "Remove bookmark" : "Bookmark message"}
          >
            <Bookmark className={`w-3.5 h-3.5 ${isBookmarked ? "text-amber-500 fill-amber-500" : "text-zinc-400"}`} />
          </button>
        )}
        {onForward && (
          <button
            onClick={onForward}
            className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
            title="Forward to channel"
          >
            <Forward className="w-3.5 h-3.5 text-zinc-400" />
          </button>
        )}
        {onPin && (
          <button
            onClick={onPin}
            className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
            title={(message.metadata as any)?.pinned ? "Unpin message" : "Pin message"}
          >
            <Pin className={`w-3.5 h-3.5 ${(message.metadata as any)?.pinned ? "text-blue-500" : "text-zinc-400"}`} />
          </button>
        )}
        {isOwn && (
          <button
            onClick={() => { setIsEditing(true); setEditContent(message.content || ""); }}
            className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
            title="Edit"
            aria-label="Edit message"
          >
            <Pencil className="w-3.5 h-3.5 text-zinc-400" />
          </button>
        )}
        {canDelete && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title={isAdminOrOwner && !isOwn ? "Delete message (Admin)" : "Delete message"}
            aria-label="Delete message"
          >
            <Trash2 className="w-3.5 h-3.5 text-red-400" />
          </button>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Message
            </DialogTitle>
            <DialogDescription className="text-zinc-500 dark:text-zinc-400">
              Are you sure you want to delete this message? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="my-2 px-3 py-2.5 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700">
            <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">{message.sender?.name}</p>
            <p className="text-sm text-zinc-700 dark:text-zinc-300 line-clamp-3">
              {(message.content || "")
                .replace(/<@[^>]+>/g, (m) => { const n = m.match(/:(.+)>/); return n ? `@${n[1]}` : '@someone'; })
                .replace(/::cmd::.*/g, '[Command]')
                .replace(/::resource::.*/g, '[Resource]')
                .replace(/::recording::.*/g, '[Recording]')
                .replace(/::sticker::.*/g, '[Sticker]')
                .slice(0, 200) || "[Attachment]"}
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowDeleteConfirm(false);
                onDelete();
              }}
            >
              Delete Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
